import { http } from "./config";
import authHeader from "./auth-header";

const baseUrl = "/fluxo-cobranca";

const defaultBoard = {
  id: 1,
  title: "Fluxo de Cobrança",
  subtitle: "Gestão de cobranças e inadimplência",
  updatedAt: new Date().toISOString(),
};

function unwrap(response) {
  if (!response || !response.data) return [];
  return response.data.data || response.data;
}

function wrap(response, mapper) {
  const data = unwrap(response);
  if (data && data.error) {
    const error = new Error(data.message || "Erro ao processar solicitação.");
    error.response = { data };
    throw error;
  }

  return {
    ...response,
    data: mapper(data),
  };
}

function normalizeColumn(column) {
  return {
    ...column,
    id: Number(column.id),
    title: column.title || column.nome,
    description: column.description || column.descricao,
    color: column.color || column.cor,
    order: column.order || column.ordem || 0,
    type: column.type || column.tipo,
  };
}

function normalizeTag(tag) {
  if (typeof tag === "string") return { nome: tag };
  return {
    ...tag,
    id: tag.id,
    nome: tag.nome || tag.name || tag.label,
    cor: tag.cor || tag.color || "#8b5cf6",
    texto_cor: tag.texto_cor || tag.textColor || "black",
    ativa: tag.ativa !== undefined ? tag.ativa : true,
    ordem: tag.ordem || tag.order || 0,
  };
}

export function normalizeInstallment(parcel) {
  const status = parcel.status || getInstallmentStatus(parcel);
  const dueDate = parcel.dueDate || parcel.data_vencimento;
  
  // Gerar referência a partir da data de vencimento se não existir
  let reference =
    parcel.reference ||
    parcel.identificacao_parcela ||
    parcel.referencia ||
    parcel.competencia;
  if (!reference && dueDate) {
    const date = new Date(dueDate);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    reference = `${month}/${year}`;
  }
  
  return {
    ...parcel,
    id: parcel.id || parcel.id_parcela,
    reference,
    amount: parcel.amount ?? parcel.valor_parcela ?? parcel.valor,
    dueDate,
    paidDate: parcel.paidDate || parcel.data_pagamento,
    paidAmount: parcel.paidAmount ?? parcel.valor_recebido,
    type: parcel.type || parcel.tipo_parcela,
    status,
  };
}

export function normalizeAgreement(agreement) {
  return {
    ...agreement,
    id: agreement.id || agreement.id_acordo,
    title: agreement.title || agreement.descricao || `Acordo - ${agreement.parcelas_negociadas || 0}x`,
    date: agreement.date || agreement.data_acordo,
    amount: agreement.amount || agreement.valor_acordo || agreement.valor_negociado,
    installments: agreement.installments || agreement.parcelas_negociadas,
    status: agreement.status || "Registrado",
  };
}

function normalizeInteraction(interaction) {
  return {
    ...interaction,
    id: interaction.id || interaction.id_interacao,
    channel: interaction.channel || interaction.canal || "Interação",
    direction: interaction.direction || interaction.direcao || "",
    date: interaction.date || interaction.data_interacao,
    message: interaction.message || interaction.mensagem_interacao || interaction.descricao,
    responsible: interaction.responsible || interaction.responsavel,
  };
}

export function normalizeClaim(claim) {
  return {
    ...claim,
    id: claim.id || claim.id_sinistro,
    title: claim.title || claim.falecido || "Sinistro",
    date: claim.date || claim.data_sinistro,
    status: claim.status || "Registrado",
  };
}

function normalizeStageHistory(stage) {
  return {
    ...stage,
    id: stage.id,
    etapaId: Number(stage.etapaId || stage.etapa_id),
    etapaNome: stage.etapaNome || stage.etapa_nome,
    etapaTipo: stage.etapaTipo || stage.etapa_tipo,
    colaboradorId: stage.colaboradorId || stage.colaborador_id,
    colaboradorNome: stage.colaboradorNome || stage.colaborador_nome,
    dtInicio: stage.dtInicio || stage.dt_inicio,
    dtFim: stage.dtFim || stage.dt_fim,
  };
}

function normalizeReceivedDocument(document) {
  return {
    ...document,
    id: document.id,
    ticketId: document.ticketId || document.ticket_id,
    renegId: document.renegId || document.reneg_id,
    documentoId: document.documentoId || document.documento_id,
    dataPagamento: document.dataPagamento || document.data_pagamento,
    valorPago: Number(document.valorPago || document.valor_pago || 0),
    createdAt: document.createdAt || document.created_at,
    updatedAt: document.updatedAt || document.updated_at,
  };
}

function normalizeCollaborator(collaborator) {
  return {
    ...collaborator,
    id: collaborator.id,
    name: collaborator.name || collaborator.nome,
    cpf: collaborator.cpf,
    company: collaborator.company || collaborator.empresa,
    role: collaborator.role || collaborator.cargo,
    department: collaborator.department || collaborator.departamento,
    active:
      collaborator.active !== undefined ? collaborator.active : collaborator.status,
  };
}

function normalizeCard(card) {
  const tags = Array.isArray(card.tags)
    ? card.tags.map(normalizeTag).filter(Boolean)
    : [];

  const amount = card.amount || card.valor_em_aberto || card.valor || 0;
  const installments = Array.isArray(card.installments)
    ? card.installments.map(normalizeInstallment)
    : [];
  const agreements = Array.isArray(card.agreements)
    ? card.agreements.map(normalizeAgreement)
    : [];
  const interactions = Array.isArray(card.interactions)
    ? card.interactions.map(normalizeInteraction)
    : [];
  const claims = Array.isArray(card.claims)
    ? card.claims.map(normalizeClaim)
    : [];
  const stageId = Number(
    card.stage || card.etapa_id || (card.etapa && card.etapa.id) || card.etapa,
  );
  const stageHistory = Array.isArray(card.etapas)
    ? card.etapas.map(normalizeStageHistory)
    : [];
  const receivedDocuments = Array.isArray(card.documentos_recebidos)
    ? card.documentos_recebidos.map(normalizeReceivedDocument)
    : [];

  return {
    ...card,
    id: card.id,
    code: card.code || card.numero_contrato || card.id_contrato || card.id,
    contractNumber: card.contractNumber || card.numero_contrato,
    contractType: card.contractType || card.tipo_contrato,
    isFalecido: Boolean(card.isFalecido || card.is_falecido),
    document: card.document || card.cpf_cnpj,
    stage: Number.isNaN(stageId) ? null : stageId,
    stageName: card.stageName || card.etapa_nome || (card.etapa && card.etapa.nome),
    stageType: card.stageType || card.etapa_tipo || (card.etapa && card.etapa.tipo),
    customerName: card.customerName || card.nome_cliente,
    description: card.description || card.descricao || "",
    amount,
    dueDate: card.dueDate || card.data_vencimento || card.data_interacao,
    interactionDate: card.interactionDate || card.data_interacao,
    contractDate: card.contractDate || card.data_contrato,
    createdAt: card.createdAt || card.created_at || card.data_interacao,
    lastContactAt: card.lastContactAt || card.ultimo_contato_em,
    lastContactUser: card.lastContactUser || card.ultimo_contato_usuario,
    loteRef: card.loteRef || card.lote_ref,
    renegId: card.renegId || card.reneg_id,
    responsible: card.responsible || card.responsavel || "Sem responsável",
    priority: card.priority || card.prioridade || "media",
    tags,
    segment: card.segment || card.segmento || "Operacao",
    customerLabel: card.customerLabel || card.faixa_atraso || "Carteira",
    delayDays: card.delayDays || card.dias_atraso || 0,
    openInstallments:
      card.openInstallments || card.quantidade_parcelas_abertas || 0,
    overdueInstallments:
      card.overdueInstallments || card.quantidade_parcelas_vencidas || 0,
    phone: card.phone || card.telefone,
    email: card.email,
    invoice: card.invoice || card.fatura,
    operator: card.operator || card.responsavel,
    plan: card.plan || {
      name: card.nome_plano,
      description: "Mensalidade recorrente",
      monthlyAmount: card.valor,
    },
    installments,
    agreements,
    interactions,
    claims,
    stageHistory,
    etapas: stageHistory,
    receivedDocuments,
    documentosRecebidos: receivedDocuments,
    documentos_recebidos: receivedDocuments,
  };
}

function getInstallmentStatus(parcel) {
  if (parcel.data_pagamento || parcel.paidDate) return "paga";

  const dueDate = parcel.data_vencimento || parcel.dueDate;
  if (!dueDate) return "a_vencer";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today ? "vencida" : "a_vencer";
}

export default {
  getBoard() {
    return Promise.resolve({
      status: 200,
      data: defaultBoard,
    });
  },

  getColumns() {
    return http
      .get(`${baseUrl}/etapas`, {
        headers: authHeader(),
      })
      .then((response) => wrap(response, (columns) => columns.map(normalizeColumn)));
  },

  getCards(params = {}) {
    return http
      .get(`${baseUrl}/tickets`, {
        params,
        headers: authHeader(),
      })
      .then((response) => wrap(response, (cards) => cards.map(normalizeCard)));
  },

  exportCardsXls(params = {}) {
    return http.get(`${baseUrl}/tickets-xls`, {
      params,
      headers: authHeader(),
      responseType: "blob",
    });
  },

  getMeta(params = {}) {
    return http.get(`${baseUrl}/meta`, {
      params,
      headers: authHeader(),
    });
  },

  getRecoveredAmount(params = {}) {
    return http.get(`${baseUrl}/valor-resgatado`, {
      params,
      headers: authHeader(),
    });
  },

  getCard(id) {
    return http
      .get(`${baseUrl}/tickets/${id}`, {
        headers: authHeader(),
      })
      .then((response) => wrap(response, normalizeCard));
  },

  getInstallments(id) {
    return http
      .get(`${baseUrl}/tickets/${id}/parcelas`, {
        headers: authHeader(),
      })
      .then((response) =>
        wrap(response, (installments) => installments.map(normalizeInstallment)),
      );
  },

  getAgreements(id) {
    return http
      .get(`${baseUrl}/tickets/${id}/acordos`, {
        headers: authHeader(),
      })
      .then((response) =>
        wrap(response, (agreements) => agreements.map(normalizeAgreement)),
      );
  },

  getInteractions(id) {
    return http
      .get(`${baseUrl}/tickets/${id}/interacoes`, {
        headers: authHeader(),
      })
      .then((response) =>
        wrap(response, (interactions) => interactions.map(normalizeInteraction)),
      );
  },

  getClaims(id) {
    return http
      .get(`${baseUrl}/tickets/${id}/sinistros`, {
        headers: authHeader(),
      })
      .then((response) => wrap(response, (claims) => claims.map(normalizeClaim)));
  },

  getProtocols(id) {
    return http.get(`${baseUrl}/tickets/${id}/protocolos`, {
      headers: authHeader(),
    });
  },

  getProtocolMessages(id, protocolNumber) {
    return http.get(
      `${baseUrl}/tickets/${id}/protocolos/${encodeURIComponent(
        protocolNumber,
      )}/mensagens`,
      {
        headers: authHeader(),
      },
    );
  },

  getRenegotiations(idTicket) {
    return http.get(`${baseUrl}/tickets/${idTicket}/renegociacoes`, {
      headers: authHeader(),
    });
  },

  getTags() {
    return http.get(`${baseUrl}/tags`, {
      headers: authHeader(),
    });
  },

  getCollaborators(params = {}) {
    return http
      .get(`${baseUrl}/colaboradores`, {
        params,
        headers: authHeader(),
      })
      .then((response) =>
        wrap(response, (collaborators) =>
          collaborators.map(normalizeCollaborator),
        ),
      );
  },

  getCardTags(ticketId) {
    return http.get(`${baseUrl}/tickets/${ticketId}/tags`, {
      headers: authHeader(),
    });
  },

  addCardTag(id, tagId) {
    return http.post(
      `${baseUrl}/tickets/${id}/tags`,
      { tagId },
      { headers: authHeader() },
    );
  },

  removeCardTag(id, tagId) {
    return http.delete(`${baseUrl}/tickets/${id}/tags/${tagId}`, {
      headers: authHeader(),
    });
  },

  updateCardStage(id, stage, payload = {}) {
    return http
      .put(
        `${baseUrl}/tickets/${id}/etapa`,
        { etapaId: stage, ...payload },
        { headers: authHeader() },
      )
      .then((response) => wrap(response, normalizeCard));
  },

  assignRenegotiation(id, renegId) {
    return http
      .put(
        `${baseUrl}/tickets/${id}/renegociacao`,
        { renegId },
        { headers: authHeader() },
      )
      .then((response) => wrap(response, normalizeCard));
  },

  assignResponsible(id, cpf, empresa) {
    return http
      .put(
        `${baseUrl}/tickets/${id}/responsavel`,
        { cpf, empresa },
        { headers: authHeader() },
      )
      .then((response) => wrap(response, normalizeCard));
  },

  createCard() {
    return Promise.reject(
      new Error("Criacao manual de ticket ainda nao existe no backend."),
    );
  },

  updateCard() {
    return Promise.reject(
      new Error("Atualizacao manual de ticket ainda nao existe no backend."),
    );
  },
};
